export interface PendingStamp {
  id: string;
  session_id: string;
  timestamp_type: string;
  vessel_id?: string;
  recorded_at: string;
  alcohol_value?: number;
  alcohol_manual?: boolean;
  face_image?: string;   // オフライン出勤時の本人確認写真(dataURL)。同期成功で端末から削除される
  latitude?: number;
  longitude?: number;
  note?: string;
  work_type?: string;
  queued_at: string;
  attempts: number;
  local_id?: string;
  user_id?: string;   // 打刻した本人（共有端末で正しい帰属のトークンを使って同期するため）
}

const QUEUE_KEY = 'catms_sync_queue';
const LAST_SYNC_KEY = 'catms_last_sync';

export function getQueue(): PendingStamp[] {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]'); }
  catch { return []; }
}

function saveQueue(q: PendingStamp[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

export function enqueue(item: Omit<PendingStamp, 'id' | 'queued_at' | 'attempts'>): PendingStamp {
  const uuid = crypto.randomUUID();
  const entry: PendingStamp = {
    ...item,
    id: uuid,
    local_id: item.local_id ?? uuid,
    queued_at: new Date().toISOString(),
    attempts: 0,
  };
  const q = getQueue();
  q.push(entry);
  saveQueue(q);
  return entry;
}

export function removeFromQueue(id: string) {
  saveQueue(getQueue().filter(x => x.id !== id));
}

export function getPendingCount(): number {
  return getQueue().length;
}

export function getLastSyncTime(): string | null {
  return localStorage.getItem(LAST_SYNC_KEY);
}

export function markSynced() {
  localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
}

const MAX_ATTEMPTS = 5; // この回数失敗したら諦めてキューから削除

/**
 * キューを順番にサーバーへ送信する。
 * - 4xx エラー（セッション消滅など）→ キューから削除して次へ
 * - ネットワーク/5xx エラー → 停止して次回に持ち越し
 * @param postItem 1件を送信する関数。user_id を渡すので、共有端末では本人のトークンで送信できる。
 * @returns 送信成功件数
 */
export async function processQueue(
  postItem: (body: Record<string, unknown>, userId?: string) => Promise<unknown>
): Promise<{ synced: number; failed: number }> {
  const q = getQueue();
  if (q.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (const item of q) {
    // 最大試行回数を超えたら諦めて削除
    if (item.attempts >= MAX_ATTEMPTS) {
      console.warn('[syncQueue] 最大試行回数超過のため削除:', item.timestamp_type, item.recorded_at);
      removeFromQueue(item.id);
      continue;
    }

    try {
      const body: Record<string, unknown> = {
        timestamp_type: item.timestamp_type,
        recorded_at:    item.recorded_at,
        session_id:     item.session_id,
        local_id:       item.local_id ?? item.id,
      };
      if (item.vessel_id)               body.vessel_id     = item.vessel_id;
      if (item.alcohol_value !== undefined) body.alcohol_value = item.alcohol_value;
      if (item.alcohol_manual)          body.alcohol_manual = true;
      if (item.face_image)              body.face_image    = item.face_image;
      if (item.latitude  !== undefined) body.latitude      = item.latitude;
      if (item.longitude !== undefined) body.longitude     = item.longitude;
      if (item.note)                    body.note          = item.note;
      if (item.work_type)               body.work_type     = item.work_type;

      await postItem(body, item.user_id);
      removeFromQueue(item.id);
      synced++;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      // 4xx 系（セッション消滅・バリデーションエラー）はリトライしても無意味なので削除
      const is4xx = /セッションが見つかりません|既に出勤中|vessel_id|BAD_REQUEST|NOT_FOUND|HTTP 4/.test(msg);
      if (is4xx) {
        console.warn('[syncQueue] 4xxエラーのためキューから削除:', msg);
        removeFromQueue(item.id);
        continue; // 次のアイテムへ（順序依存だが、消えたセッションなら続行できる）
      }
      // ネットワークエラー/5xx → attempts++ して停止（次回に持ち越し）
      saveQueue(getQueue().map(x => x.id === item.id ? { ...x, attempts: x.attempts + 1 } : x));
      failed++;
      break;
    }
  }

  if (synced > 0) markSynced();
  return { synced, failed };
}
